import type {
  RecordAnswer,
  RecordAnswers,
  RecordBlock,
  RecordQuestion,
  YesNoAnswer,
} from '@hue/contracts';
import { Combobox, DatePicker, Input, MultiSelect, RichTextEditor } from '@hue/design-system';
import { Check } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { RichText } from './shared.js';
import { cn } from './helpers.js';

/// A ficha como quem responde a vê: os blocos na ordem do modelo, cada pergunta com o controle do
/// seu tipo. Serve à ficha do cliente e à pré-visualização do construtor.
///
/// `hintFor` devolve o que outras fichas e a equipe já disseram sobre o tema da pergunta — é o
/// aviso que impede a alergia declarada em uma ficha de ser esquecida na outra.
export function RecordForm({
  blocks,
  answers,
  onChange,
  errors = {},
  today,
  hintFor,
}: {
  blocks: readonly RecordBlock[];
  answers: RecordAnswers;
  onChange: (answers: RecordAnswers) => void;
  errors?: Readonly<Record<string, string>>;
  today: string;
  hintFor?: (question: RecordQuestion, answer: RecordAnswer | undefined) => ReactNode;
}) {
  const set = (id: string, value: RecordAnswer | undefined) => {
    const next = { ...answers };
    if (value === undefined) delete next[id];
    else next[id] = value;
    onChange(next);
  };
  return (
    <div className="grid gap-5">
      {blocks.map((block) => {
        if (block.kind === 'HEADING') {
          return (
            <h3 key={block.id} className="text-lg font-bold tracking-[-.02em]">
              {block.text}
            </h3>
          );
        }
        if (block.kind === 'SUBHEADING') {
          return (
            <h4
              key={block.id}
              className="border-t border-surface-border pt-4 text-sm font-semibold text-content-secondary"
            >
              {block.text}
            </h4>
          );
        }
        if (block.kind === 'DESCRIPTION') {
          return <RichText key={block.id} html={block.html} className="text-content-muted" />;
        }
        return (
          <div key={block.id} className="grid gap-2" data-question={block.id}>
            <QuestionField
              question={block}
              value={answers[block.id]}
              error={errors[block.id]}
              today={today}
              onValue={(value) => set(block.id, value)}
            />
            {hintFor?.(block, answers[block.id])}
          </div>
        );
      })}
    </div>
  );
}

function QuestionField({
  question,
  value,
  error,
  today,
  onValue,
}: {
  question: RecordQuestion;
  value: RecordAnswer | undefined;
  error: string | undefined;
  today: string;
  onValue: (value: RecordAnswer | undefined) => void;
}) {
  const label = `${question.label}${question.required ? ' *' : ''}`;
  const hint = question.help ?? undefined;
  switch (question.type) {
    case 'SHORT_TEXT':
    case 'NUMBER':
      return (
        <Input
          label={label}
          hint={hint}
          error={error}
          value={(value as string | undefined) ?? ''}
          inputMode={question.type === 'NUMBER' ? 'decimal' : undefined}
          className={question.type === 'NUMBER' ? 'max-w-40' : undefined}
          onChange={(event) => onValue(event.target.value || undefined)}
        />
      );
    case 'LONG_TEXT':
      return (
        <RichTextEditor
          label={label}
          hint={hint}
          error={error}
          value={(value as string | undefined) ?? ''}
          placeholder="Escreva aqui"
          onValueChange={(html) => onValue(html || undefined)}
        />
      );
    case 'DATE':
      return (
        <DatePicker
          label={label}
          hint={hint}
          error={error}
          today={today}
          value={(value as string | undefined) ?? null}
          onValueChange={(date) => onValue(date ?? undefined)}
          className="max-w-56"
        />
      );
    case 'DECLARATION':
      return (
        <Group label={label} hint={hint} error={error} hideLabel>
          {(labelId) => (
            <CheckBox
              checked={value === true}
              labelledBy={labelId}
              invalid={Boolean(error)}
              onToggle={() => onValue(value === true ? undefined : true)}
            >
              {label}
            </CheckBox>
          )}
        </Group>
      );
    case 'YES_NO':
      return (
        <YesNo
          question={question}
          label={label}
          hint={hint}
          error={error}
          value={value as YesNoAnswer | undefined}
          onValue={onValue}
        />
      );
    case 'SINGLE_CHOICE':
      return (
        <Group label={label} hint={hint} error={undefined}>
          {() => (
            <Combobox
              label={label}
              hideLabel
              placeholder="Buscar opção"
              selectedLabel={question.options.find((option) => option.id === value)?.label ?? ''}
              options={question.options.map((option) => ({
                value: option.id,
                label: option.label,
              }))}
              error={error}
              onClear={() => onValue(undefined)}
              onSelect={(choice) => {
                if (choice.kind === 'option') onValue(choice.value);
              }}
            />
          )}
        </Group>
      );
    case 'MULTIPLE_CHOICE': {
      const picked = (value as string[] | undefined) ?? [];
      return (
        <Group label={label} hint={hint} error={undefined}>
          {() => (
            <MultiSelect
              label={label}
              hideLabel
              itemNoun="opções"
              error={error}
              exclusiveValue={question.exclusiveOptionId ?? undefined}
              options={question.options.map((option) => ({
                value: option.id,
                label: option.label,
              }))}
              selected={picked}
              onChange={(next) => {
                const ordered = question.options
                  .map((option) => option.id)
                  .filter((id) => next.includes(id));
                onValue(ordered.length ? ordered : undefined);
              }}
            />
          )}
        </Group>
      );
    }
  }
}

function YesNo({
  question,
  label,
  hint,
  error,
  value,
  onValue,
}: {
  question: RecordQuestion;
  label: string;
  hint: string | undefined;
  error: string | undefined;
  value: YesNoAnswer | undefined;
  onValue: (value: RecordAnswer | undefined) => void;
}) {
  return (
    <Group label={label} hint={hint} error={error}>
      {(labelId) => (
        <div className="grid gap-2">
          <div
            role="radiogroup"
            aria-labelledby={labelId}
            aria-invalid={Boolean(error)}
            className="flex flex-wrap gap-2"
          >
            {(
              [
                ['Sim', true],
                ['Não', false],
              ] as const
            ).map(([text, answer]) => (
              <Choice
                key={text}
                role="radio"
                selected={value?.value === answer}
                onClick={() =>
                  onValue(
                    value?.value === answer
                      ? undefined
                      : { value: answer, detail: answer ? (value?.detail ?? null) : null },
                  )
                }
              >
                {text}
              </Choice>
            ))}
          </div>
          {question.detailLabel && value?.value ? (
            <Input
              label={question.detailLabel}
              value={value.detail ?? ''}
              onChange={(event) => onValue({ value: true, detail: event.target.value || null })}
            />
          ) : null}
        </div>
      )}
    </Group>
  );
}

/// Pergunta cujo controle não é um campo só: o rótulo é um texto apontado pelo grupo, e é ele que o
/// Resumo de erros lê para escrever o nome da pergunta.
function Group({
  label,
  hint,
  error,
  hideLabel = false,
  children,
}: {
  label: string;
  hint: string | undefined;
  error: string | undefined;
  hideLabel?: boolean;
  children: (labelId: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="grid gap-2">
      <span id={id} className={cn('text-xs font-semibold text-content/70', hideLabel && 'sr-only')}>
        {label}
      </span>
      {hint ? <p className="-mt-1 text-xs leading-5 text-content-muted">{hint}</p> : null}
      {children(id)}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Choice({
  selected,
  onClick,
  role,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  role: 'radio';
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        'min-h-10 cursor-pointer rounded-xl border px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
        selected
          ? 'border-brand-500 bg-brand-500 text-on-brand'
          : 'border-surface-border bg-surface-elevated text-content hover:border-brand-500/50',
      )}
    >
      {children}
    </button>
  );
}

function CheckBox({
  checked,
  onToggle,
  labelledBy,
  invalid,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  labelledBy?: string;
  invalid?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-invalid={invalid}
      onClick={onToggle}
      className="flex min-h-10 w-full cursor-pointer items-start gap-3 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-content/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
    >
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 grid size-5 shrink-0 place-items-center rounded-md border transition',
          checked ? 'border-brand-500 bg-brand-500 text-on-brand' : 'border-surface-border',
        )}
      >
        {checked ? <Check className="size-3.5" /> : null}
      </span>
      <span>{children}</span>
    </button>
  );
}
